/**
 * Moldura das telas públicas de acesso (login, redefinir senha).
 *
 * 2 colunas em desktop: brand panel (grid + tagline) + coluna do formulário.
 * Em mobile, o brand colapsa para um header compacto. Extraído do LoginPage
 * (PSHWO-67053) para o /reset-password ter a mesma cara sem duplicar markup.
 */

import type { ReactNode } from "react";

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-screen grid-cols-1 bg-[var(--color-background)] lg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]">
      {/* Brand panel — desktop only */}
      <aside className="relative hidden overflow-hidden bg-[var(--color-card)] lg:flex lg:flex-col lg:justify-between lg:p-12">
        {/* Grid pattern de fundo */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(var(--color-border-strong) 1px, transparent 1px), linear-gradient(90deg, var(--color-border-strong) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        {/* Brand mark + wordmark */}
        <div className="relative flex items-center gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary-soft-strong)] font-mono text-xl font-bold text-[var(--color-primary)]"
          >
            $
          </span>
          <span className="text-[20px] font-semibold tracking-tight text-[var(--color-foreground)]">
            price<span className="font-mono text-[var(--color-primary)]">$</span>how
          </span>
        </div>

        {/* Tagline central */}
        <div className="relative max-w-md">
          <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-primary)]">
            Painel Operacional
          </div>
          <h1 className="text-[40px] font-semibold leading-[1.05] tracking-[-0.02em] text-[var(--color-foreground)]">
            Suba a planilha.{" "}
            <span className="text-[var(--color-primary)]">Suas TVs se viram.</span>
          </h1>
          <p className="mt-4 text-[15px] leading-[1.55] text-[var(--color-muted-foreground)]">
            Importe preços do ERP, gere artes em IA e mantenha todas as TVs da
            sua rede sincronizadas em tempo real.
          </p>
        </div>

        {/* Footer brand panel */}
        <div className="relative flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
          <span>
            Priceshow · Bauru, SP ·{" "}
            <span className="tabular-nums">v{__APP_VERSION__}</span>
          </span>
          <a
            href="https://priceshow.tv"
            className="hover:text-[var(--color-foreground)]"
          >
            priceshow.tv
          </a>
        </div>
      </aside>

      {/* Form column */}
      <section className="flex flex-col items-center justify-center px-6 py-10 lg:px-12">
        {/* Brand compacto — mobile */}
        <div className="mb-8 flex items-center gap-2.5 lg:hidden">
          <span
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-md bg-[var(--color-primary-soft-strong)] font-mono text-base font-bold text-[var(--color-primary)]"
          >
            $
          </span>
          <span className="text-[17px] font-semibold tracking-tight text-[var(--color-foreground)]">
            price<span className="font-mono text-[var(--color-primary)]">$</span>how
          </span>
        </div>

        <div className="w-full max-w-sm">{children}</div>
      </section>
    </main>
  );
}
